import { useEffect, useMemo, useState } from "react";
import { RotateCcw, Save, Scaling, Trash2, Plus, Wand2, Check, Bell, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import "@/components/teamproj/TeamProjections.css";

// Admin editor for the final PTS scoring calibration: a global % haircut + optional per-team overrides.
export function CalibrationCard() {
  const [data, setData] = useState(null);
  const [globalPct, setGlobalPct] = useState(5);
  const [busy, setBusy] = useState(false);
  const [newTeam, setNewTeam] = useState("");
  const [newPct, setNewPct] = useState(5);

  const refresh = (d) => { setData(d); setGlobalPct(d.global_pct); };

  useEffect(() => {
    api.get("/points-calibration").then((r) => refresh(r.data)).catch((e) => toast.error(errorText(e)));
    api.get("/points-calibration/weekly").then((r) => setWeekly(r.data.run)).catch(() => {});
  }, []);

  const [weekly, setWeekly] = useState(null);
  const applyWeekly = async () => {
    setBusy(true);
    try {
      const r = await api.post("/points-calibration/weekly/apply");
      refresh({ ...data, ...r.data }); setWeekly(null);
      toast.success(`Εφαρμόστηκε η εβδομαδιαία πρόταση (${r.data.applied} ομάδες)`);
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const dismissWeekly = async () => {
    try { await api.post("/points-calibration/weekly/dismiss"); setWeekly(null); toast.info("Η εβδομαδιαία πρόταση απορρίφθηκε"); }
    catch (e) { toast.error(errorText(e)); }
  };

  const toggleAuto = async () => {
    const next = !data.auto_apply;
    setBusy(true);
    try {
      const r = await api.put("/points-calibration/auto-mode", { enabled: next });
      refresh({ ...data, ...r.data });
      toast.success(next ? "Αυτόματη εφαρμογή: ΕΝΕΡΓΗ" : "Αυτόματη εφαρμογή: ΑΝΕΝΕΡΓΗ");
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };

  const codeOf = useMemo(() => {
    const m = {};
    (data?.teams_list || []).forEach((t) => { m[t.id] = t.code; });
    return m;
  }, [data]);

  const saveGlobal = async () => {
    setBusy(true);
    try {
      const r = await api.put("/points-calibration", { global_pct: Number(globalPct) });
      refresh({ ...data, ...r.data });
      toast.success(`Γενικό ποσοστό: −${Number(r.data.global_pct)}%`);
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };

  const saveTeam = async (teamId, pct) => {
    setBusy(true);
    try {
      const r = await api.put("/points-calibration/team", { team_id: teamId, pct: Number(pct) });
      refresh({ ...data, ...r.data });
      setNewTeam(""); setNewPct(Number(globalPct));
      toast.success(`${codeOf[teamId] || teamId}: −${Number(pct)}%`);
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };

  const clearTeam = async (teamId) => {
    setBusy(true);
    try {
      const r = await api.delete("/points-calibration/team", { params: { team_id: teamId } });
      refresh({ ...data, ...r.data });
      toast.success(`${codeOf[teamId] || teamId}: επαναφορά στο γενικό`);
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };

  const [analysis, setAnalysis] = useState(null);
  const loadAnalysis = async () => {
    setBusy(true);
    try {
      const r = await api.get("/points-calibration/analysis");
      setAnalysis(r.data);
      const c = Object.keys(r.data.suggested || {}).length;
      toast[c ? "success" : "info"](c ? `${c} προτάσεις από ${r.data.overall?.n || 0} ματς` : "Δεν υπάρχουν αρκετά δεδομένα για πρόταση ακόμη");
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const applyAuto = async () => {
    setBusy(true);
    try {
      const r = await api.post("/points-calibration/auto-apply");
      refresh({ ...data, ...r.data });
      setAnalysis(null);
      toast.success(`Εφαρμόστηκαν ${r.data.applied} προτάσεις ανά ομάδα`);
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };

  if (!data) return null;
  const teamEntries = Object.entries(data.teams || {});
  const available = (data.teams_list || []).filter((t) => !(t.id in (data.teams || {})));

  return (
    <div className="tp-root mt-6">
      <div className="tp-card" data-testid="calibration-card">
        <h4><Scaling size={13} className="inline mr-1" />ΒΑΘΜΟΝΟΜΗΣΗ ΠΟΝΤΩΝ <small>(admin · μείωση % στους προβλεπόμενους πόντους κάθε παίκτη — σύνολα &amp; σκορ ακολουθούν)</small></h4>

        {weekly && (weekly.suggested && Object.keys(weekly.suggested).length > 0) && (
          <div data-testid="calibration-weekly-banner" style={{ margin: "8px 0", padding: "10px 12px", borderRadius: 8, border: "1px solid #3a6", background: "rgba(95,240,192,0.08)" }}>
            <p style={{ color: "#8ff", fontWeight: 700, margin: 0 }}><Bell size={13} className="inline mr-1" />
              {weekly.status === "auto_applied"
                ? `Αυτόματη εβδομαδιαία βαθμονόμηση (${weekly.week})`
                : `Εβδομαδιαία πρόταση βαθμονόμησης (${weekly.week})`}</p>
            <p className="tp-note" style={{ marginTop: 4 }}>
              {weekly.status === "auto_applied"
                ? `Εφαρμόστηκαν αυτόματα αλλαγές σε ${Object.keys(weekly.suggested).length} ομάδες βάσει ιστορικού bias.`
                : `${Object.keys(weekly.suggested).length} ομάδες με προτεινόμενη αλλαγή βάσει ιστορικού bias.`}</p>
            {(weekly.teams || []).map((t) => (
              <div key={t.team_id} className="tp-note" data-testid={`calibration-weekly-team-${t.team_id}`}>
                {t.code}: −{t.current_pct}% → <b style={{ color: "#5ff0c0" }}>−{t.suggested_pct}%</b> <span style={{ opacity: 0.7 }}>(bias {t.bias_after > 0 ? "+" : ""}{t.bias_after}, {t.n} ματς)</span>
              </div>
            ))}
            <div className="tv-vactions">
              {weekly.status === "auto_applied" ? (
                <button type="button" className="tv-vbtn primary" onClick={dismissWeekly} disabled={busy} data-testid="calibration-weekly-ack"><Check size={14} />Το είδα</button>
              ) : (
                <>
                  <button type="button" className="tv-vbtn primary" onClick={applyWeekly} disabled={busy} data-testid="calibration-weekly-apply"><Check size={14} />Εφαρμογή</button>
                  <button type="button" className="tv-vbtn" onClick={dismissWeekly} disabled={busy} data-testid="calibration-weekly-dismiss">Απόρριψη</button>
                </>
              )}
            </div>
          </div>
        )}

        <div className="tv-vform tv-margins" style={{ alignItems: "center" }}>
          <label style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={!!data.auto_apply} onChange={toggleAuto} disabled={busy} data-testid="calibration-auto-mode-toggle" style={{ width: 16, height: 16 }} />
            <span><Zap size={13} className="inline mr-1" />Αυτόματη εφαρμογή εβδομαδιαίας πρότασης</span>
          </label>
          <span className="tp-note" style={{ alignSelf: "center" }}>
            {data.auto_apply ? "Οι εβδομαδιαίες προτάσεις εφαρμόζονται μόνες τους" : "Οι προτάσεις χρειάζονται έγκριση"}
          </span>
        </div>

        <div className="tv-vform tv-margins">
          <label><small>Γενικό ποσοστό μείωσης (όλες οι ομάδες)</small>
            <input type="number" step="0.5" min={data.min_pct} max={data.max_pct} value={globalPct}
                   onChange={(e) => setGlobalPct(e.target.value)} data-testid="calibration-global" /></label>
          <span className="tp-note" style={{ alignSelf: "center" }}>π.χ. 100 πόντοι → {(100 * (1 - Number(globalPct) / 100)).toFixed(1)} (−{Number(globalPct) || 0}%)</span>
        </div>
        <div className="tv-vactions">
          <button type="button" className="tv-vbtn primary" onClick={saveGlobal} disabled={busy} data-testid="calibration-global-save"><Save size={14} />{busy ? "…" : "Αποθήκευση"}</button>
          <button type="button" className="tv-vbtn" onClick={() => setGlobalPct(data.default_pct)} disabled={busy} data-testid="calibration-global-default"><RotateCcw size={14} />Προεπιλογή ({data.default_pct}%)</button>
        </div>

        <p className="nc-section-label" style={{ marginTop: 16 }}>Ανά ομάδα (υπερισχύει του γενικού)</p>
        {teamEntries.length === 0 && <p className="tp-note">Καμία ειδική ρύθμιση — όλες οι ομάδες στο γενικό −{Number(globalPct) || 0}%.</p>}
        {teamEntries.map(([tid, pct]) => (
          <div key={tid} className="tv-vform tv-margins" data-testid={`calibration-team-${tid}`} style={{ marginTop: 6 }}>
            <label style={{ minWidth: 90 }}><small>{codeOf[tid] || tid}</small>
              <input type="number" step="0.5" min={data.min_pct} max={data.max_pct} defaultValue={pct}
                     onBlur={(e) => Number(e.target.value) !== Number(pct) && saveTeam(tid, e.target.value)} data-testid={`calibration-team-pct-${tid}`} /></label>
            <span className="tp-note" style={{ alignSelf: "center" }}>−{Number(pct)}%</span>
            <button type="button" className="tv-vbtn" onClick={() => clearTeam(tid)} disabled={busy} data-testid={`calibration-team-remove-${tid}`}><Trash2 size={14} />Αφαίρεση</button>
          </div>
        ))}

        <div className="tv-vform tv-margins" style={{ marginTop: 10 }}>
          <label style={{ minWidth: 150 }}><small>Προσθήκη ομάδας</small>
            <select value={newTeam} onChange={(e) => setNewTeam(e.target.value)} data-testid="calibration-team-select">
              <option value="">— Διάλεξε ομάδα —</option>
              {available.map((t) => <option key={t.id} value={t.id}>{t.code} · {t.name}</option>)}
            </select></label>
          <label style={{ minWidth: 90 }}><small>%</small>
            <input type="number" step="0.5" min={data.min_pct} max={data.max_pct} value={newPct}
                   onChange={(e) => setNewPct(e.target.value)} data-testid="calibration-new-pct" /></label>
          <button type="button" className="tv-vbtn primary" onClick={() => newTeam && saveTeam(newTeam, newPct)} disabled={busy || !newTeam} data-testid="calibration-team-add"><Plus size={14} />Προσθήκη</button>
        </div>

        <p className="tp-note">Η μείωση αφαιρεί % μόνο από τους πόντους (PTS)· REB/AST/3PM μένουν ίδια. Ισχύει σε NBA· το EuroLeague/EuroCup ακολουθεί το γενικό ποσοστό.</p>

        <p className="nc-section-label" style={{ marginTop: 16 }}>Αυτόματη βαθμονόμηση (από ιστορικό bias)</p>
        <div className="tv-vactions">
          <button type="button" className="tv-vbtn" onClick={loadAnalysis} disabled={busy} data-testid="calibration-auto-compute"><Wand2 size={14} />Υπολογισμός προτάσεων</button>
          {analysis && Object.keys(analysis.suggested || {}).length > 0 &&
            <button type="button" className="tv-vbtn primary" onClick={applyAuto} disabled={busy} data-testid="calibration-auto-apply"><Check size={14} />Εφαρμογή {Object.keys(analysis.suggested).length} προτάσεων</button>}
        </div>
        {analysis && (
          <div style={{ marginTop: 8 }} data-testid="calibration-auto-result">
            <p className="tp-note">{analysis.overall ? `Δείγμα: ${analysis.overall.n} ματς · bias χωρίς βαθμ.: ${analysis.overall.bias_before > 0 ? "+" : ""}${analysis.overall.bias_before} · με τρέχουσα: ${analysis.overall.bias_after > 0 ? "+" : ""}${analysis.overall.bias_after}` : "Χωρίς βαθμολογημένα ματς ακόμη."} · Πρόταση απαιτεί ≥{analysis.min_games} ματς &amp; |bias| ≥ {analysis.min_bias} ανά ομάδα.</p>
            {analysis.teams.filter((t) => t.has_suggestion).map((t) => (
              <div key={t.team_id} className="tp-note" data-testid={`calibration-suggestion-${t.team_id}`}>
                {t.code}: −{t.current_pct}% → <b style={{ color: "#5ff0c0" }}>−{t.suggested_pct}%</b> <span style={{ opacity: 0.7 }}>(bias {t.bias_after > 0 ? "+" : ""}{t.bias_after}, {t.n} ματς)</span>
              </div>
            ))}
            {Object.keys(analysis.suggested || {}).length === 0 && <p className="tp-note" style={{ opacity: 0.7 }}>Καμία ομάδα δεν έχει αρκετό σταθερό bias για αυτόματη πρόταση ακόμη.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
